import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, List, LayoutGrid, Package, Check } from 'lucide-react';
import { PageHeader } from '@/cajita/ui';
import { CxDrawer } from '@/cajita/overlay';
import { useConfirm } from '@/shared/modals/ConfirmDialog';
import { Money } from '@/shared/display/Money';
import { Can } from '@/features/platform/Can';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { listProducts, createProduct, updateProduct, deleteProduct, type Product, type ProductInput } from './products.api';
import { ProductForm } from './ProductForm';

const TAX_LABEL: Record<Product['taxCategory'], string> = {
  GRAVADO: 'Gravado',
  EXONERADO: 'Exonerado',
  INAFECTO: 'Inafecto',
  EXPORTACION: 'Exportacion',
  GRATUITO: 'Gratuito',
};

function Thumb({ p, size = 36 }: { p: Product; size?: number }): JSX.Element {
  if (p.imageUrl) return <img src={p.imageUrl} alt="" style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover', flex: 'none' }} />;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        fontSize: size * 0.42,
        fontWeight: 800,
        color: '#0B3A47',
        background: 'linear-gradient(135deg,#E2F8FF,#ABEFFF)',
      }}
    >
      {p.name.charAt(0).toUpperCase()}
    </div>
  );
}

export function ProductsPage(): JSX.Element {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const [view, setView] = useState<'tabla' | 'grid'>('tabla');
  const search = useDebouncedValue(searchRaw, 300);
  const [drawer, setDrawer] = useState<{ mode: 'create' } | { mode: 'edit'; product: Product } | null>(null);

  const query = useQuery({
    queryKey: ['products', { page, search }],
    queryFn: () => listProducts({ page, pageSize: 20, search: search || undefined }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['products'] });

  const createMut = useMutation({
    mutationFn: (input: ProductInput) => createProduct(input),
    onSuccess: () => {
      toast.success('Producto creado');
      setDrawer(null);
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; input: Partial<ProductInput> }) => updateProduct(args.id, args.input),
    onSuccess: () => {
      toast.success('Producto actualizado');
      setDrawer(null);
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      toast.success('Producto eliminado');
      invalidate();
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const handleDelete = async (p: Product) => {
    const ok = await confirm({ title: '¿Eliminar producto?', description: `Se desactivará "${p.name}".`, variant: 'danger', confirmLabel: 'Eliminar' });
    if (ok) deleteMut.mutate(p.id);
  };

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Productos"
          sub={`${total} productos · afectación IGV y precio`}
          actions={
            <>
              <div className="seg">
                <button className={view === 'tabla' ? 'on' : ''} onClick={() => setView('tabla')} title="Tabla">
                  <List size={15} />
                </button>
                <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} title="Cuadrícula">
                  <LayoutGrid size={15} />
                </button>
              </div>
              <Can perm="products.write">
                <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'create' })}>
                  <Plus size={17} />
                  Nuevo producto
                </button>
              </Can>
            </>
          }
        />

        <div className="card">
          <div className="tbl-toolbar">
            <div className="input-icon" style={{ width: 320 }}>
              <Search size={17} />
              <input
                className="input"
                placeholder="Buscar por nombre o SKU…"
                value={searchRaw}
                onChange={(e) => {
                  setSearchRaw(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <span className="mla t-sm muted">{total} en catálogo</span>
          </div>

          {query.isLoading ? (
            <SkeletonRows cols={5} />
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <Package size={28} />
              </div>
              <div>
                <div className="t-h2" style={{ marginBottom: 4 }}>
                  {search ? 'Sin resultados' : 'Sin productos aún'}
                </div>
                <div className="muted">{search ? 'Prueba otra búsqueda.' : 'Registra tu primer producto.'}</div>
              </div>
              {!search && (
                <Can perm="products.write">
                  <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'create' })}>
                    <Plus size={17} />
                    Nuevo producto
                  </button>
                </Can>
              )}
            </div>
          ) : view === 'grid' ? (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14, padding: 18 }}>
              {items.map((p) => (
                <div key={p.id} className="card" style={{ overflow: 'hidden', cursor: 'pointer' }} onClick={() => setDrawer({ mode: 'edit', product: p })}>
                  <div style={{ height: 90 }}>
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontSize: 34, fontWeight: 800, color: '#0B3A47', background: 'linear-gradient(135deg,#E2F8FF,#ABEFFF)' }}>
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: 12 }}>
                    <div className="fw6 t-sm" style={{ minHeight: 34, lineHeight: 1.25 }}>
                      {p.name}
                    </div>
                    <div className="row between aic" style={{ marginTop: 6 }}>
                      <span className="tnum fw8">
                        <Money value={p.price} />
                      </span>
                      <span className={`badge badge-${p.active ? 'green' : 'gray'}`}>{p.active ? 'Activo' : 'Inactivo'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Producto</th>
                    <th>Afectación IGV</th>
                    <th className="right">Precio</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ mode: 'edit', product: p })}>
                      <td className="tnum muted">{p.sku}</td>
                      <td>
                        <div className="row aic gap10">
                          <Thumb p={p} size={36} />
                          <span className="strong">{p.name}</span>
                        </div>
                      </td>
                      <td className="t-sm">{TAX_LABEL[p.taxCategory]}</td>
                      <td className="right tnum strong">
                        <Money value={p.price} />
                      </td>
                      <td>
                        <span className={`badge badge-${p.active ? 'green' : 'gray'}`}>{p.active ? 'Activo' : 'Inactivo'}</span>
                      </td>
                      <td className="right" onClick={(e) => e.stopPropagation()}>
                        <Can perm="products.write">
                          <div className="row gap4" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDrawer({ mode: 'edit', product: p })} title="Editar">
                              <Pencil size={16} />
                            </button>
                            <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} onClick={() => handleDelete(p)} title="Eliminar">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </Can>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 20 && (
            <div className="tbl-foot">
              <span>
                Mostrando {items.length} de {total}
              </span>
              <div className="row gap6">
                <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <span className="t-sm muted" style={{ padding: '0 6px' }}>
                  {page} / {totalPages}
                </span>
                <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <CxDrawer
          title={drawer.mode === 'edit' ? 'Editar producto' : 'Nuevo producto'}
          sub={drawer.mode === 'edit' ? drawer.product.sku : 'Registra un producto o servicio'}
          onClose={() => setDrawer(null)}
          footer={
            <>
              <button className="btn btn-secondary grow" onClick={() => setDrawer(null)}>
                Cancelar
              </button>
              <button className="btn btn-primary grow" type="submit" form="product-form" disabled={createMut.isPending || updateMut.isPending}>
                <Check size={18} />
                {createMut.isPending || updateMut.isPending ? 'Guardando…' : drawer.mode === 'edit' ? 'Guardar' : 'Crear producto'}
              </button>
            </>
          }
        >
          <ProductForm
            formId="product-form"
            defaultValues={drawer.mode === 'edit' ? drawer.product : null}
            onSubmit={(values) => {
              if (drawer.mode === 'edit') updateMut.mutate({ id: drawer.product.id, input: values });
              else createMut.mutate(values);
            }}
          />
        </CxDrawer>
      )}
    </div>
  );
}

function SkeletonRows({ cols, rows = 7 }: { cols: number; rows?: number }): JSX.Element {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="sk" style={{ height: 12, flex: c === 1 ? 2 : 1, opacity: 1 - r * 0.08 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function extractError(err: unknown): string {
  const resp = (err as { response?: { data?: { message?: string } } }).response?.data;
  return resp?.message ?? 'Ocurrió un error';
}
